import React, { useEffect, useState, useRef, useCallback } from "react";
import { Table } from "../../../../ui/components/Table";
import { Button } from "../../../../ui/components/Button";
import { Badge } from "../../../../ui/components/Badge";
import { TextField } from "../../../../ui/components/TextField";
import { DropdownMenu } from "../../../../ui/components/DropdownMenu";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import {
  FeatherAlertCircle,
  FeatherClock,
  FeatherFileText,
  FeatherCheckCircle,
  FeatherEye,
  FeatherUpload,
  FeatherChevronDown,
  FeatherSearch,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherMessageCircle,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import {
  TenantComplianceEntity,
  ComplianceStatus,
} from "../../domain/entities/TenantComplianceEntity";
import { ComplianceDetailPanel } from "../components/ComplianceDetailPanel";
import QueryRaiseModal from "../../../../shared/queries/presentation/components/QueryRaiseModal";
import { getCompliances } from "../../data/api";
import { ComplianceMapper } from "../../data/mappers/ComplianceMapper";

export const ComplianceDashboardPage: React.FC = () => {
  const [compliances, setCompliances] = useState<TenantComplianceEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedComplianceId, setSelectedComplianceId] = useState<
    number | null
  >(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Pagination states
  const [itemsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Query Raise Modal states
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [selectedComplianceForQuery, setSelectedComplianceForQuery] =
    useState<TenantComplianceEntity | null>(null);

  const fetchCompliances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: {
        status?: string;
        date_filter?: string;
        search?: string;
        limit?: number;
        offset?: number;
      } = {};

      // Handle status filter - if overdue, use date_filter instead
      if (statusFilter !== "all" && statusFilter !== "overdue") {
        filters.status = statusFilter;
      }

      // Handle date filter - if status is overdue, use date_filter for overdue
      if (statusFilter === "overdue") {
        filters.date_filter = "overdue";
      } else if (dateFilter !== "all") {
        filters.date_filter = dateFilter;
      }

      // Add search query
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      // Add pagination
      const offset = (currentPage - 1) * itemsPerPage;
      filters.limit = itemsPerPage;
      filters.offset = offset;

      // Call API directly to get pagination info
      const apiResponse = await getCompliances(filters);

      if (apiResponse.success && apiResponse.data) {
        // Apply overdue logic on frontend
        const processedResult = apiResponse.data.map((compliance: any) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const dueDate = new Date(compliance.due_date);
          dueDate.setHours(0, 0, 0, 0);

          // If due date has passed and status is not completed/approved/sent_for_query, mark as overdue
          if (
            dueDate < today &&
            compliance.status !== "completed" &&
            compliance.status !== "approved" &&
            compliance.status !== "sent_for_query"
          ) {
            return { ...compliance, status: "overdue" as ComplianceStatus };
          }

          return compliance;
        });

        // Map to entities
        const entities =
          ComplianceMapper.toTenantComplianceEntityArray(processedResult);
        setCompliances(entities);

        // Extract pagination from API response
        if (apiResponse.pagination) {
          setTotal(apiResponse.pagination.total);
          setHasMore(apiResponse.pagination.has_more);
          const calculatedTotalPages = Math.ceil(
            apiResponse.pagination.total / apiResponse.pagination.limit
          );
          setTotalPages(calculatedTotalPages);
        } else {
          // Fallback if no pagination (backward compatibility)
          const calculatedTotal =
            processedResult.length === itemsPerPage
              ? currentPage * itemsPerPage + 1
              : currentPage * itemsPerPage;
          setTotal(calculatedTotal);
          setHasMore(processedResult.length === itemsPerPage);
          setTotalPages(Math.ceil(calculatedTotal / itemsPerPage));
        }
      } else {
        setCompliances([]);
        setTotal(0);
        setHasMore(false);
        setTotalPages(1);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch compliances")
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter, searchQuery, currentPage, itemsPerPage]);

  // Debounce search query changes
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchCompliances();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Fetch when status or date filters change (immediate, no debounce)
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchCompliances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFilter]);

  // Fetch when page changes
  useEffect(() => {
    fetchCompliances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getStatusBadge = (status: ComplianceStatus) => {
    switch (status) {
      case "overdue":
        return <Badge variant="error">Overdue</Badge>;
      case "pending":
        return <Badge variant="warning">Pending</Badge>;
      case "under_review":
        return <Badge>Under Review</Badge>;
      case "completed":
      case "approved":
        return <Badge variant="success">Completed</Badge>;
      case "sent_for_query":
        return <Badge variant="neutral">Query Raised</Badge>;
      default:
        return <Badge variant="warning">Pending</Badge>;
    }
  };

  const getStatusIcon = (status: ComplianceStatus) => {
    switch (status) {
      case "overdue":
        return <FeatherAlertCircle />;
      case "pending":
        return <FeatherClock />;
      case "under_review":
        return <FeatherFileText />;
      case "completed":
      case "approved":
        return <FeatherCheckCircle />;
      case "sent_for_query":
        return <FeatherMessageCircle />;
      default:
        return <FeatherFileText />;
    }
  };

  const getStatusIconVariant = (
    status: ComplianceStatus
  ): "error" | "warning" | "neutral" | "success" => {
    switch (status) {
      case "overdue":
        return "error";
      case "pending":
        return "warning";
      case "under_review":
        return "neutral";
      case "completed":
      case "approved":
        return "success";
      case "sent_for_query":
        return "neutral";
      default:
        return "neutral";
    }
  };

  const isOverdue = (dueDate: string, status: ComplianceStatus): boolean => {
    if (status === "completed" || status === "approved") {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDueDateColor = (
    dueDate: string,
    status: ComplianceStatus
  ): string => {
    if (status === "completed" || status === "approved") {
      return "text-neutral-500";
    }
    if (isOverdue(dueDate, status)) {
      return "text-error-600";
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const daysUntilDue = Math.ceil(
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilDue <= 7) {
      return "text-warning-600";
    }
    return "text-neutral-500";
  };

  const canUploadProof = (status: ComplianceStatus): boolean => {
    return (
      status !== "completed" &&
      status !== "approved" &&
      status !== "sent_for_query"
    );
  };

  const canRaiseQuery = (status: ComplianceStatus): boolean => {
    return status !== "sent_for_query" && status !== "approved";
  };

  const handleRaiseQuery = (compliance: TenantComplianceEntity) => {
    setSelectedComplianceForQuery(compliance);
    setIsQueryModalOpen(true);
  };

  const handleQueryRaised = (queryCreated: boolean) => {
    setIsQueryModalOpen(false);
    if (queryCreated && selectedComplianceForQuery) {
      // Update the compliance status locally instead of refreshing
      setCompliances((prev) =>
        prev.map((c) =>
          c.id === selectedComplianceForQuery.id
            ? { ...c, status: "sent_for_query" as ComplianceStatus }
            : c
        )
      );
    }
    setSelectedComplianceForQuery(null);
  };

  const getActionButton = (compliance: TenantComplianceEntity) => {
    // If query has been raised, show message
    if (compliance.status === "sent_for_query") {
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            size="small"
            icon={<FeatherMessageCircle />}
            disabled={true}
          >
            Query Raised
          </Button>
        </div>
      );
    }

    if (compliance.status === "under_review") {
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            size="small"
            onClick={() => setSelectedComplianceId(compliance.id)}
          >
            In Review
          </Button>
          {canRaiseQuery(compliance.status) && (
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherMessageCircle />}
              onClick={() => handleRaiseQuery(compliance)}
            >
              Raise Query
            </Button>
          )}
        </div>
      );
    }

    if (compliance.status === "completed" || compliance.status === "approved") {
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            size="small"
            icon={<FeatherEye />}
            onClick={() => setSelectedComplianceId(compliance.id)}
          >
            View
          </Button>
          {canRaiseQuery(compliance.status) && (
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherMessageCircle />}
              onClick={() => handleRaiseQuery(compliance)}
            >
              Raise Query
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="neutral-primary"
          size="small"
          icon={<FeatherUpload />}
          onClick={() => setSelectedComplianceId(compliance.id)}
          disabled={!canUploadProof(compliance.status)}
        >
          Upload Proof
        </Button>
        {canRaiseQuery(compliance.status) && (
          <Button
            variant="neutral-secondary"
            size="small"
            icon={<FeatherMessageCircle />}
            onClick={() => handleRaiseQuery(compliance)}
          >
            Raise Query
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6">
      {/* Header Section */}
      <div className="flex w-full flex-col items-start gap-6 px-8 py-6">
        <div className="flex flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Compliance To-Do
          </span>
          <span className="text-body font-body text-subtext-color">
            Upload proofs and submit compliances for senior accountant review
          </span>
        </div>
        <div className="flex w-full items-center gap-3">
          <TextField
            className="h-auto w-64 flex-none"
            variant="filled"
            label=""
            helpText=""
            icon={<FeatherSearch />}
          >
            <TextField.Input
              placeholder="Search by compliance name..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
            />
          </TextField>
          <span className="text-body font-body text-subtext-color">
            Filter by:
          </span>
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <Button
                variant="neutral-secondary"
                size="small"
                iconRight={<FeatherChevronDown />}
              >
                Status:{" "}
                {statusFilter === "all"
                  ? "All"
                  : statusFilter === "pending"
                  ? "Pending"
                  : statusFilter === "under_review"
                  ? "Under Review"
                  : statusFilter === "completed"
                  ? "Completed"
                  : statusFilter === "overdue"
                  ? "Overdue"
                  : statusFilter === "sent_for_query"
                  ? "Query Raised"
                  : "All"}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="start"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    onClick={() => setStatusFilter("all")}
                  >
                    All
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setStatusFilter("pending")}
                  >
                    Pending
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setStatusFilter("under_review")}
                  >
                    Under Review
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setStatusFilter("completed")}
                  >
                    Completed
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setStatusFilter("overdue")}
                  >
                    Overdue
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setStatusFilter("sent_for_query")}
                  >
                    Query Raised
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <Button
                variant="neutral-secondary"
                size="small"
                iconRight={<FeatherChevronDown />}
                disabled={statusFilter === "overdue"}
              >
                Due:{" "}
                {dateFilter === "all"
                  ? "All"
                  : dateFilter === "upcoming"
                  ? "Upcoming (7 days)"
                  : dateFilter === "soon"
                  ? "Soon (30 days)"
                  : dateFilter === "later"
                  ? "Later"
                  : dateFilter === "overdue"
                  ? "Overdue"
                  : "All"}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="start"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    onClick={() => setDateFilter("all")}
                  >
                    All
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setDateFilter("upcoming")}
                  >
                    Upcoming (7 days)
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setDateFilter("soon")}
                  >
                    Soon (30 days)
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setDateFilter("later")}
                  >
                    Later
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    onClick={() => setDateFilter("overdue")}
                  >
                    Overdue
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex w-full grow shrink-0 basis-0 items-start gap-6 px-8 pb-8">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4">
          {error && (
            <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-sm font-medium text-red-800">
                {error.message}
              </span>
            </div>
          )}
          {loading ? (
            <div className="flex w-full items-center justify-center p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-body font-body text-subtext-color">
                  Loading compliances...
                </p>
              </div>
            </div>
          ) : compliances.length === 0 ? (
            <div className="flex w-full items-center justify-center p-8 text-body font-body text-subtext-color">
              No compliances found.
            </div>
          ) : (
            <>
              <Table
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Compliance Name</Table.HeaderCell>
                    <Table.HeaderCell>Period</Table.HeaderCell>
                    <Table.HeaderCell>Due Date</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Action</Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {compliances.map((compliance) => (
                  <Table.Row key={compliance.id}>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <IconWithBackground
                          variant={getStatusIconVariant(compliance.status)}
                          size="small"
                          icon={getStatusIcon(compliance.status)}
                        />
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {compliance.compliance_form_name}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-neutral-500">
                        {compliance.period}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span
                        className={`whitespace-nowrap text-body font-body ${getDueDateColor(
                          compliance.due_date,
                          compliance.status
                        )}`}
                      >
                        {formatDate(compliance.due_date)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{getStatusBadge(compliance.status)}</Table.Cell>
                    <Table.Cell>{getActionButton(compliance)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table>

              {/* Pagination Controls */}
              {totalPages > 1 && !loading && (
                <div className="flex w-full items-center justify-between mt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                      {Math.min(currentPage * itemsPerPage, total)} of {total}{" "}
                      results
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      icon={<FeatherChevronLeft />}
                    >
                      Previous
                    </Button>

                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <Button
                              key={pageNum}
                              variant={
                                currentPage === pageNum
                                  ? "brand-primary"
                                  : "neutral-tertiary"
                              }
                              size="small"
                              onClick={() => setCurrentPage(pageNum)}
                              className="min-w-[32px]"
                            >
                              {pageNum}
                            </Button>
                          );
                        }
                      )}
                    </div>

                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={!hasMore && currentPage >= totalPages}
                      icon={<FeatherChevronRight />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Compliance Detail Panel */}
      {selectedComplianceId && (
        <ComplianceDetailPanel
          complianceId={selectedComplianceId}
          onClose={() => setSelectedComplianceId(null)}
          onRefresh={fetchCompliances}
          onStatusUpdate={(complianceId, newStatus) => {
            // Update the compliance status locally
            setCompliances((prev) =>
              prev.map((c) =>
                c.id === complianceId
                  ? { ...c, status: newStatus as ComplianceStatus }
                  : c
              )
            );
          }}
        />
      )}

      {/* Query Raise Modal */}
      {isQueryModalOpen && selectedComplianceForQuery && (
        <QueryRaiseModal
          open={isQueryModalOpen}
          onOpenChange={(open) => {
            setIsQueryModalOpen(open);
            if (!open) {
              setSelectedComplianceForQuery(null);
            }
          }}
          sourceDoc={{
            type: "compliance",
            id: selectedComplianceForQuery.id,
            filename: selectedComplianceForQuery.compliance_form_name,
          }}
          complianceType={selectedComplianceForQuery.compliance_type}
          initialValues={{
            title: `Query regarding ${selectedComplianceForQuery.compliance_form_name}`,
            description: "",
            priority: "Medium",
          }}
          onQueryRaised={() => handleQueryRaised(true)}
        />
      )}
    </div>
  );
};
